/**
 * MilestoneCelebration
 *
 * Single mounted instance (member-only). Polls for unacknowledged payment
 * milestones and celebrates them with a toast — plus a confetti burst for
 * the headline "Fully Paid" moment — then acknowledges so it shows once.
 */
import { useEffect, useRef } from 'react';
import confetti from 'canvas-confetti';
import { toast } from 'sonner';
import { useAuth } from '@/utils/auth';
import apiClient from '@/utils/api';

const fireConfetti = () => {
  const duration = 2600;
  const end = Date.now() + duration;
  const colors = ['#10b981', '#2563eb', '#f59e0b', '#ec4899', '#8b5cf6'];
  (function frame() {
    confetti({ particleCount: 5, angle: 60, spread: 60, origin: { x: 0 }, colors });
    confetti({ particleCount: 5, angle: 120, spread: 60, origin: { x: 1 }, colors });
    if (Date.now() < end) requestAnimationFrame(frame);
  })();
  // Center pop
  confetti({ particleCount: 140, spread: 90, origin: { y: 0.6 }, colors, scalar: 1.1 });
};

const MilestoneCelebration = () => {
  const { user } = useAuth();
  const inFlight = useRef(false);

  useEffect(() => {
    if (!user || user.role !== 'member') return;

    let cancelled = false;

    const celebrate = async () => {
      if (inFlight.current) return;
      inFlight.current = true;
      try {
        const res = await apiClient.get('/members/milestones/pending');
        const milestones = res.data?.milestones || [];
        if (cancelled || milestones.length === 0) return;

        milestones.forEach((m, i) => {
          setTimeout(() => {
            if (m.confetti) fireConfetti();
            const isFull = m.milestone_key === 'pct_100';
            toast.success(
              isFull ? `🎉 ${m.label}!` : `🌟 Milestone reached: ${m.label}`,
              {
                description: isFull
                  ? "You've fully paid your membership — thank you for your commitment!"
                  : `You're ${m.pct_at_achievement}% through your payment journey. Keep it up!`,
                duration: isFull ? 8000 : 6000,
              }
            );
          }, i * 900);
        });

        const ids = milestones.map((m) => m.milestone_id);
        await apiClient.post('/members/milestones/acknowledge', { milestone_ids: ids });
      } catch (e) {
        // analytics/celebration must never disrupt the member experience
      } finally {
        inFlight.current = false;
      }
    };

    celebrate();
    const interval = setInterval(celebrate, 60000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [user]);

  return null;
};

export default MilestoneCelebration;
